import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import Button from '../../../../common/Button';
import api from '../../../../../services/api';
import Spinner from '../../../../common/Spinner';
import DeleteWarning from '../../common/DeleteWarning';
import CouponModal from './CouponModal';
import { NotificationType, Position } from '../../../../../types/enums/notificationEnums';
import { useNotification } from '../../../../../context/NotificationContext';
import { formatDate } from '../../../../../utils/dateFormatter';

// Coupon interface to match API response
interface Coupon {
  id: number;
  code: string;
  description: string;
  discount_type: 'fixed' | 'percentage';
  discount_value: number;
  min_cart_total: number;
  start_date: string;
  end_date: string;
  usage_limit: number;
  used_count: number;
  is_active: boolean;
}

const Coupons = () => {
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [warningModal, setWarningModal] = useState<boolean>(false);
  const [selectedCoupon, setSelectedCoupon] = useState<Coupon | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Fetch all coupons on component mount
  useEffect(() => {
    fetchCoupons();
  }, []);

  // Function to fetch all coupons
  const fetchCoupons = async () => {
    try {
      setLoading(true);
      const response = await api.get('/coupons');
      setCoupons(response.data.data || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching coupons:', err);
      setError('Failed to fetch coupons. Please try again later.');
      showNotification(
        t('Error occurred fetching coupons. Please try again later.'),
        NotificationType.Error,
        Position.TopRight
      );
    } finally {
      setLoading(false);
    }
  };

  // Function to handle opening the edit modal for a coupon
  const handleEdit = (coupon: Coupon) => {
    setSelectedCoupon(coupon);
    setModalOpen(true);
  };

  // Function to handle creating a new coupon
  const handleNew = () => {
    setSelectedCoupon(null);
    setModalOpen(true);
  };

  // Function to handle showing delete warning
  const handleWarning = (coupon: Coupon) => {
    setSelectedCoupon(coupon);
    setWarningModal(true);
  };

  // Function to handle deleting a coupon
  const handleDeleteCoupon = async () => {
    if (selectedCoupon) {
      try {
        await api.delete(`/coupons/${selectedCoupon.id}`);
        setCoupons(coupons.filter(coupon => coupon.id !== selectedCoupon.id));
        setWarningModal(false);
        setSelectedCoupon(null);
        showNotification(
          t('Coupon deleted successfully.'),
          NotificationType.Success,
          Position.TopRight
        );
      } catch (err) {
        console.error('Error deleting coupon:', err);
        showNotification(
          t('Error occurred deleting coupon. Please try again later.'),
          NotificationType.Error,
          Position.TopRight
        );
      }
    }
  };

  if (error) {
    return <div className="text-red-500">{error}</div>;
  }

  return (
    <div className="w-full text-stone-900 dark:text-zinc-300 pb-4">
      <div>
        <h1 className="flex justify-start items-center pt-4 font-bold text-[40px] text-stone-900 dark:text-orange-600">
          {t('Coupons')}
        </h1>
        <p>{t('Manage discount coupons for your store')}</p>
      </div>

      <div className="flex justify-end mb-4 mt-6">
        <Button variant="primary" onClick={handleNew} className="flex items-center gap-2">
          <FontAwesomeIcon icon={faPlus} />
          {t('Add New Coupon')}
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center mt-10">
          <Spinner />
        </div>
      ) : (
        <div className="h-full">
          <div className="w-full h-full overflow-auto rounded-md">
            <table className="w-full table-fixed border-collapse">
              <thead className="bg-slate-300 dark:bg-stone-800">
                <tr>
                  <th className="w-2/12 px-4 py-2 text-left border border-gray-600 dark:border-gray-400">
                    {t('Code')}
                  </th>
                  <th className="w-2/12 px-4 py-2 text-left border border-gray-600 dark:border-gray-400">
                    {t('Description')}
                  </th>
                  <th className="w-1/12 px-4 py-2 text-left border border-gray-600 dark:border-gray-400">
                    {t('Discount')}
                  </th>
                  <th className="w-1/12 px-4 py-2 text-left border border-gray-600 dark:border-gray-400">
                    {t('Start Date')}
                  </th>
                  <th className="w-1/12 px-4 py-2 text-left border border-gray-600 dark:border-gray-400">
                    {t('End Date')}
                  </th>
                  <th className="w-1/12 px-4 py-2 text-left border border-gray-600 dark:border-gray-400">
                    {t('Usage')}
                  </th>
                  <th className="w-1/12 px-4 py-2 text-left border border-gray-600 dark:border-gray-400">
                    {t('Status')}
                  </th>
                  <th className="w-2/12 px-4 py-2 text-left border border-gray-600 dark:border-gray-400">
                    {t('Actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="text-stone-900 dark:text-zinc-300 bg-slate-200 dark:bg-stone-700">
                {coupons.length === 0 ? (
                  <tr>
                    <td
                      colSpan={9}
                      className="px-4 py-4 text-center border border-gray-600 dark:border-gray-400"
                    >
                      {t('No coupons found')}
                    </td>
                  </tr>
                ) : (
                  coupons.map(coupon => (
                    <tr key={coupon.id} className="hover:bg-slate-300 dark:hover:bg-stone-600">
                      <td className="px-4 py-2 border border-gray-600 dark:border-gray-400 font-semibold">
                        {coupon.code}
                      </td>
                      <td className="px-4 py-2 border border-gray-600 dark:border-gray-400">
                        <div
                          className="line-clamp-2 overflow-hidden text-ellipsis"
                          title={coupon.description}
                        >
                          {coupon.description}
                        </div>
                      </td>
                      <td className="px-4 py-2 border border-gray-600 dark:border-gray-400">
                        {coupon.discount_type === 'percentage'
                          ? `${coupon.discount_value}%`
                          : `${coupon.discount_value}€`}
                      </td>
                      <td className="px-1.5 py-2 border border-gray-600 dark:border-gray-400">
                        {formatDate(coupon.start_date)}
                      </td>
                      <td className="px-1.5 py-2 border border-gray-600 dark:border-gray-400">
                        {formatDate(coupon.end_date)}
                      </td>
                      <td className="px-4 py-2 border border-gray-600 dark:border-gray-400">
                        {coupon.used_count} / {coupon.usage_limit}
                      </td>
                      <td className="px-4 py-2 border border-gray-600 dark:border-gray-400">
                        <span
                          className={
                            coupon.is_active
                              ? 'text-green-500 font-semibold'
                              : 'text-red-500 font-semibold'
                          }
                        >
                          {coupon.is_active ? t('Active') : t('Inactive')}
                        </span>
                      </td>
                      <td className="px-3 py-2 border border-gray-600 dark:border-gray-400">
                        <div className="flex flex-row w-full gap-2">
                          <Button
                            className="w-1/2"
                            variant="tertiary"
                            onClick={() => handleEdit(coupon)}
                            data-tooltip-content={t('Edit Coupon')}
                            data-tooltip-id="tooltip"
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </Button>
                          <Button
                            variant="cancel"
                            className="w-1/2"
                            onClick={() => handleWarning(coupon)}
                            data-tooltip-content={t('Delete Coupon')}
                            data-tooltip-id="tooltip"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Coupon Modal for Create/Edit */}
      <CouponModal
        modalOpen={modalOpen}
        setModalOpen={setModalOpen}
        coupon={selectedCoupon}
        onSuccess={fetchCoupons}
      />

      {/* Delete Warning Modal */}
      <DeleteWarning
        modalOpen={warningModal}
        setModalOpen={setWarningModal}
        onConfirm={handleDeleteCoupon}
        description={t(
          'Are you sure you want to delete this coupon? This action cannot be undone.'
        )}
      />
    </div>
  );
};

export default Coupons;
